import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Heart, Menu, Plus } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { useFavorites } from "@/lib/favorites";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useMe } from "@/lib/api";
import { DesktopNav, MobileNav } from "@/components/MainNav";

const UserLinks = ({ mobile }) => {
  const { data: me } = useMe();
  const cls = mobile ? "font-sub text-lg" : "font-sub text-sm text-foreground/80 hover:text-terracotta transition-colors";
  return (
    <>
      <Link to="/meine-veranstaltungen" className={cls} data-testid={`nav-my-events${mobile ? "-mobile" : ""}`}>Mein Bereich</Link>
      <Link to="/konto" className={cls} data-testid={`nav-account${mobile ? "-mobile" : ""}`}>Konto</Link>
      {me?.role === "admin" && (
        <Link to="/admin" className={cls} data-testid={`nav-admin${mobile ? "-mobile" : ""}`}>Admin</Link>
      )}
    </>
  );
};

const FavLink = ({ mobile }) => {
  const { ids } = useFavorites();
  return (
    <Link to="/merkliste" data-testid={`nav-favorites${mobile ? "-mobile" : ""}`}
      className={mobile ? "flex items-center gap-2 font-sub text-lg" : "relative flex items-center text-foreground/80 transition-colors hover:text-terracotta"} title="Merkliste">
      <Heart size={mobile ? 18 : 20} strokeWidth={1.5} />{mobile && "Merkliste"}
      {ids.length > 0 && (
        <span className={`${mobile ? "" : "absolute -right-2 -top-2"} flex h-4 min-w-4 items-center justify-center rounded-full bg-terracotta px-1 text-[0.6rem] text-white`} data-testid="nav-favorites-count">{ids.length}</span>
      )}
    </Link>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { key } = useLocation();
  useEffect(() => setOpen(false), [key]);
  return (
  <header className="sticky top-0 z-50 border-b border-white/40 bg-shell/80 backdrop-blur-xl" data-testid="site-header">
    <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-3 px-4 md:h-[72px] md:px-10">
      <Link to="/" className="flex shrink-0 items-center gap-2.5 whitespace-nowrap" data-testid="nav-logo">
        <LogoMark />
        <span className="font-display text-lg leading-none sm:text-xl">WattNu <span className="italic text-terracotta">Ostfriesland</span></span>
      </Link>
      <DesktopNav />
      <div className="flex items-center gap-3 md:gap-4">
        <span className="hidden md:flex"><FavLink /></span>
        <Show when="signed-in"><div className="hidden items-center gap-5 min-[1380px]:flex"><UserLinks /></div></Show>
        <Link to="/einreichen" data-testid="nav-submit-event" aria-label="Veranstaltung einreichen" title="Veranstaltung einreichen"
          className="lift hidden items-center gap-1.5 bg-terracotta px-4 py-2.5 font-sub text-sm text-white hover:bg-terracotta/90 sm:inline-flex">
          <Plus size={16} strokeWidth={1.75} /> <span className="lg:hidden xl:inline">Veranstaltung einreichen</span>
        </Link>
        <Show when="signed-out">
          <Link to="/sign-in" className="hidden font-sub text-sm underline-offset-4 hover:underline md:inline" data-testid="nav-sign-in">Anmelden</Link>
        </Show>
        <Show when="signed-in"><UserButton /></Show>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger className="-mr-2 flex h-11 w-11 items-center justify-center min-[1380px]:hidden" data-testid="nav-mobile-toggle" aria-label="Menü öffnen"><Menu strokeWidth={1.5} /></SheetTrigger>
          <SheetContent side="right" className="overflow-y-auto bg-shell pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-6">
            <SheetTitle className="font-display text-2xl">Menü</SheetTitle>
            <MobileNav />
            <div className="mt-8 flex flex-col gap-5 border-t border-foreground/10 pt-6">
              <NavLink to="/einreichen" className="font-sub text-lg text-terracotta" data-testid="nav-submit-mobile">Veranstaltung einreichen</NavLink>
              <FavLink mobile />
              <Show when="signed-in"><UserLinks mobile /></Show>
              <Show when="signed-out"><NavLink to="/sign-in" className="font-sub text-lg" data-testid="nav-sign-in-mobile">Anmelden</NavLink></Show>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  </header>
  );
};
